"use client";

import { useState } from "react";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { LoadingState, EmptyState, ErrorState, PageHeader } from "@/components/features/states";
import { UpgradeToProCard } from "@/components/features/upgrade-to-pro-card";
import {
  Store as StoreIcon,
  Plus,
  Package,
  ShoppingBag,
  TrendingUp,
  Wallet,
  Star,
  Clock,
  CheckCircle2,
  Trash2,
  Edit3,
  Utensils,
  AlertCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { currencySymbol } from "@/lib/format";
import type { Product } from "@/lib/types";

export function MerchantDashboardView() {
  const { user } = useAuthStore();
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [productSheetOpen, setProductSheetOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [createStoreOpen, setCreateStoreOpen] = useState(false);

  // Load merchant's store.
  const { data: storeData, isLoading: storeLoading } = useQuery({
    queryKey: ["store", "me"],
    queryFn: () => api.stores.me(),
  });
  const store = storeData?.data?.store;

  // Load dashboard analytics (only if store exists + approved).
  const { data: dashData, isLoading: dashLoading } = useQuery({
    queryKey: ["store", "dashboard"],
    queryFn: () => api.stores.dashboard(),
    enabled: !!store && store.status === "approved",
  });
  const stats = dashData?.data?.stats;
  const popularItems = dashData?.data?.popularItems ?? [];
  const recentOrders = dashData?.data?.recentOrders ?? [];

  // Load merchant's products.
  const { data: productsData } = useQuery({
    queryKey: ["store", "products"],
    queryFn: () => api.stores.products(),
    enabled: !!store,
  });
  const products = productsData?.data?.products ?? [];

  // Load merchant's orders.
  const { data: ordersData } = useQuery({
    queryKey: ["store", "orders"],
    queryFn: () => api.stores.orders(),
    enabled: !!store,
  });
  const orders = ordersData?.data?.orders ?? [];

  const updateOrderStatus = useMutation({
    mutationFn: ({ jobId, action }: { jobId: string; action: "confirm" | "preparing" | "ready" }) =>
      api.stores.updateOrderStatus(jobId, action),
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(t("common.confirm"));
        qc.invalidateQueries({ queryKey: ["store", "orders"] });
        qc.invalidateQueries({ queryKey: ["store", "dashboard"] });
      } else {
        toast.error(res.error);
      }
    },
  });

  const deleteProduct = useMutation({
    mutationFn: (id: string) => api.stores.deleteProduct(id),
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(t("common.delete"));
        qc.invalidateQueries({ queryKey: ["store", "products"] });
      } else toast.error(res.error);
    },
  });

  const toggleProductAvail = useMutation({
    mutationFn: ({ id, available }: { id: string; available: boolean }) =>
      api.stores.updateProduct(id, { available }),
    onSuccess: (res) => {
      if (res.ok) qc.invalidateQueries({ queryKey: ["store", "products"] });
    },
  });

  const toggleStoreActive = useMutation({
    mutationFn: (active: boolean) => api.stores.update({ active }),
    onSuccess: (res) => {
      if (res.ok) {
        qc.invalidateQueries({ queryKey: ["store", "me"] });
        toast.success(res.data?.store?.active ? t("merchant.resumeStore") : t("merchant.pauseStore"));
      }
    },
  });

  if (storeLoading) return <LoadingState />;

  // No store yet — show create-store CTA.
  if (!store) {
    return (
      <div className="space-y-4">
        <PageHeader title={t("merchant.title")} subtitle={t("merchant.subtitle")} />
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex flex-col items-center gap-3 p-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <StoreIcon className="h-8 w-8" />
            </div>
            <h2 className="text-xl font-bold">{t("merchant.noStore")}</h2>
            <p className="max-w-sm text-sm text-muted-foreground">
              {t("merchant.noStoreDesc")}
            </p>
            <Button size="lg" onClick={() => setCreateStoreOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> {t("merchant.createStore")}
            </Button>
          </CardContent>
        </Card>
        <CreateStoreSheet open={createStoreOpen} onOpenChange={setCreateStoreOpen} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={`${t("merchant.title")}`}
        subtitle={store.name}
        action={
          <Button size="sm" variant="outline" onClick={() => setCreateStoreOpen(true)}>
            <Edit3 className="mr-1 h-4 w-4" /> {t("common.edit")}
          </Button>
        }
      />

      {/* Store status banner */}
      <Card
        className={
          store.status === "approved"
            ? "border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/30"
            : store.status === "pending"
            ? "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30"
            : "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30"
        }
      >
        <CardContent className="flex items-center gap-3 p-3 sm:p-4">
          {store.status === "approved" ? (
            <CheckCircle2 className="h-8 w-8 shrink-0 text-emerald-600" />
          ) : store.status === "pending" ? (
            <Clock className="h-8 w-8 shrink-0 text-amber-600" />
          ) : (
            <AlertCircle className="h-8 w-8 shrink-0 text-red-600" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">
              {store.status === "approved"
                ? t("merchant.approved")
                : store.status === "pending"
                ? t("merchant.pendingApproval")
                : store.status === "suspended"
                ? t("merchant.suspended")
                : t("merchant.rejected")}
            </p>
            <p className="text-xs text-muted-foreground">
              {store.status === "approved"
                ? `${t("merchant.tier" + (store.commissionTier.charAt(0).toUpperCase() + store.commissionTier.slice(1)))} · ${store.commissionRate}% commission`
                : store.status === "pending"
                ? t("merchant.reviewingApplication")
                : store.rejectionReason || t("merchant.contactSupport")}
            </p>
          </div>
          {store.status === "approved" && (
            <div className="flex items-center gap-2">
              <Switch
                checked={store.active}
                onCheckedChange={(v) => toggleStoreActive.mutate(v)}
              />
              <span className="text-xs text-muted-foreground">
                {store.active ? t("common.active") : t("common.inactive")}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Analytics stats (approved stores only) */}
      {store.status === "approved" && stats && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
          <StatCard icon={ShoppingBag} label={t("merchant.todaysOrders")} value={stats.todaysOrders} color="text-amber-600" />
          <StatCard icon={Wallet} label={t("merchant.weeklyRevenue")} value={`${currencySymbol()}${stats.revenueWeek.toFixed(0)}`} color="text-emerald-600" />
          <StatCard icon={TrendingUp} label={t("merchant.totalOrders")} value={stats.totalOrders} color="text-primary" />
          <StatCard icon={CheckCircle2} label={t("merchant.completedOrders")} value={stats.completedOrders} color="text-emerald-600" />
        </div>
      )}

      {/* V1.0 Monetization — Module 1: Upgrade To Pro + Boost My Store */}
      {store.status === "approved" && <UpgradeToProCard />}

      {/* Tabs: Orders / Menu / Analytics */}
      <Tabs defaultValue="orders">
        <TabsList className="w-full">
          <TabsTrigger value="orders" className="flex-1">{t("merchant.orders")}</TabsTrigger>
          <TabsTrigger value="menu" className="flex-1">{t("merchant.manageMenu")}</TabsTrigger>
          {store.status === "approved" && (
            <TabsTrigger value="analytics" className="flex-1">{t("nav.analytics")}</TabsTrigger>
          )}
        </TabsList>

        {/* Orders tab */}
        <TabsContent value="orders" className="mt-4 space-y-3">
          {store.status !== "approved" ? (
            <EmptyState
              icon={<ShoppingBag className="h-8 w-8" />}
              title={t("merchant.noOrders")}
              description={t("merchant.ordersAfterApproval")}
            />
          ) : orders.length === 0 ? (
            <EmptyState
              icon={<ShoppingBag className="h-8 w-8" />}
              title={t("merchant.noOrders")}
              description={t("merchant.noProductsDesc")}
            />
          ) : (
            orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onUpdate={(action) => updateOrderStatus.mutate({ jobId: order.id, action })}
                t={t}
              />
            ))
          )}
        </TabsContent>

        {/* Menu tab */}
        <TabsContent value="menu" className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">
              {products.length} {products.length === 1 ? "product" : "products"}
            </p>
            <Button
              size="sm"
              onClick={() => { setEditingProduct(null); setProductSheetOpen(true); }}
              disabled={store.status === "pending"}
            >
              <Plus className="mr-1 h-4 w-4" /> {t("merchant.addProduct")}
            </Button>
          </div>
          {products.length === 0 ? (
            <EmptyState
              icon={<Package className="h-8 w-8" />}
              title={t("merchant.noProducts")}
              description={t("merchant.noProductsDesc")}
              action={
                <Button size="sm" onClick={() => { setEditingProduct(null); setProductSheetOpen(true); }}>
                  <Plus className="mr-1 h-4 w-4" /> {t("merchant.addProduct")}
                </Button>
              }
            />
          ) : (
            <div className="space-y-2">
              {products.map((product) => (
                <Card key={product.id}>
                  <CardContent className="flex items-center gap-3 p-3">
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-muted">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center"><Package className="h-5 w-5 text-muted-foreground" /></div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-1 text-sm font-semibold">{product.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {product.category && `${product.category} · `}
                        {currencySymbol()} {product.price.toFixed(2)}
                        {product.discountedPrice != null && (
                          <span className="ml-1 text-amber-600">→ {currencySymbol()} {product.discountedPrice.toFixed(2)}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Switch
                        checked={product.available}
                        onCheckedChange={(v) => toggleProductAvail.mutate({ id: product.id, available: v })}
                      />
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditingProduct(product); setProductSheetOpen(true); }}>
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => deleteProduct.mutate(product.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Analytics tab */}
        {store.status === "approved" && (
          <TabsContent value="analytics" className="mt-4 space-y-4">
            {dashLoading ? (
              <LoadingState />
            ) : (
              <>
                {/* Revenue breakdown */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold">
                      <Wallet className="h-4 w-4 text-emerald-600" /> {t("merchant.revenue")}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <Row label={t("merchant.revenue") + " (today)"} value={`${currencySymbol()}${stats?.revenueToday.toFixed(2) ?? "0.00"}`} />
                      <Row label={t("merchant.weeklyRevenue")} value={`${currencySymbol()}${stats?.revenueWeek.toFixed(2) ?? "0.00"}`} />
                      <Row label={t("merchant.commission")} value={`${currencySymbol()}${stats?.commissionTotal.toFixed(2) ?? "0.00"}`} muted />
                      <div className="border-t pt-2">
                        <Row label={t("merchant.payout")} value={`${currencySymbol()}${stats?.payoutTotal.toFixed(2) ?? "0.00"}`} bold />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Popular items */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold">
                      <Star className="h-4 w-4 text-amber-500" /> {t("merchant.popularItems")}
                    </h3>
                    {popularItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No sales data yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {popularItems.map((item, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2">
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-bold">{i + 1}</span>
                              {item.name}
                            </span>
                            <span className="text-muted-foreground">
                              {item.qty} sold · {currencySymbol()}{item.revenue.toFixed(0)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Product editor sheet */}
      <ProductSheet
        open={productSheetOpen}
        onOpenChange={setProductSheetOpen}
        product={editingProduct}
        storeId={store.id}
      />

      {/* Store editor sheet */}
      <CreateStoreSheet open={createStoreOpen} onOpenChange={setCreateStoreOpen} existingStore={store} />
    </div>
  );
}

function OrderCard({
  order,
  onUpdate,
  t,
}: {
  order: any;
  onUpdate: (action: "confirm" | "preparing" | "ready") => void;
  t: (k: string) => string;
}) {
  // Parse cart from metadata.
  let cartItems: any[] = [];
  try {
    const meta = JSON.parse(order.metadata || "{}");
    cartItems = meta.cartItems || [];
  } catch {}

  const isPreparing = !!order.preparingAt;
  return (
    <Card>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-bold">#{order.code}</p>
            <p className="text-xs text-muted-foreground">
              {order.customer?.name} · {new Date(order.createdAt).toLocaleString()}
            </p>
          </div>
          <Badge variant={order.status === "completed" ? "secondary" : "default"}>
            {order.status}
          </Badge>
        </div>
        {cartItems.length > 0 && (
          <div className="mt-2 space-y-1 rounded-lg bg-muted/50 p-2 text-xs">
            {cartItems.map((item, i) => (
              <div key={i} className="flex justify-between">
                <span>{item.qty}× {item.product.name}</span>
                <span className="font-medium">{currencySymbol()}{item.lineTotal.toFixed(2)}</span>
              </div>
            ))}
            <div className="border-t pt-1 font-bold">
              {t("stores.total")}: {currencySymbol()}{(order.agreedPrice || order.customerOffer || 0).toFixed(2)}
            </div>
          </div>
        )}
        {order.dropoffAddress && (
          <p className="mt-2 text-xs text-muted-foreground">
            📍 {order.dropoffAddress}
          </p>
        )}
        {/* Action buttons */}
        {order.status === "open" && (
          <div className="mt-3 flex gap-2">
            {!isPreparing ? (
              <Button size="sm" onClick={() => onUpdate("preparing")}>
                <Utensils className="mr-1 h-3.5 w-3.5" /> {t("merchant.markPreparing")}
              </Button>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => onUpdate("ready")}>
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> {t("merchant.markReady")}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProductSheet({
  open,
  onOpenChange,
  product,
  storeId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  product: Product | null;
  storeId: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(product?.name || "");
  const [description, setDescription] = useState(product?.description || "");
  const [price, setPrice] = useState(String(product?.price ?? ""));
  const [category, setCategory] = useState(product?.category || "");
  const [imageUrl, setImageUrl] = useState(product?.imageUrl || "");
  const [available, setAvailable] = useState(product?.available ?? true);

  // Reset form when product changes.
  useState(() => {
    setName(product?.name || "");
    setDescription(product?.description || "");
    setPrice(String(product?.price ?? ""));
    setCategory(product?.category || "");
    setImageUrl(product?.imageUrl || "");
    setAvailable(product?.available ?? true);
  });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name,
        description: description || undefined,
        price: Number(price),
        category: category || undefined,
        imageUrl: imageUrl || undefined,
        available,
      };
      if (product) {
        return api.stores.updateProduct(product.id, body);
      }
      return api.stores.addProduct(body as any);
    },
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(product ? t("common.save") : t("common.create"));
        qc.invalidateQueries({ queryKey: ["store", "products"] });
        onOpenChange(false);
      } else {
        toast.error(res.error);
      }
    },
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="mx-auto max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{product ? t("common.edit") : t("merchant.addProduct")}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          <div className="space-y-1.5">
            <Label>{t("merchant.productName")} *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("merchant.productName")} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("merchant.productDescription")}</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("merchant.productPrice")} *</Label>
              <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("merchant.productCategory")}</Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder={t("merchant.productCategoryPlaceholder")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("merchant.productImage")}</Label>
            <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <Label htmlFor="avail">{t("merchant.available")}</Label>
            <Switch id="avail" checked={available} onCheckedChange={setAvailable} />
          </div>
          <Button className="w-full" disabled={!name || !price || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? t("common.loading") : t("common.save")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CreateStoreSheet({
  open,
  onOpenChange,
  existingStore,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  existingStore?: any;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(existingStore?.name || "");
  const [description, setDescription] = useState(existingStore?.description || "");
  const [category, setCategory] = useState(existingStore?.category || "restaurant");
  const [address, setAddress] = useState(existingStore?.address || "");
  const [imageUrl, setImageUrl] = useState(existingStore?.imageUrl || "");
  const [tier, setTier] = useState(existingStore?.commissionTier || "basic");

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name,
        description: description || undefined,
        category,
        address: address || undefined,
        imageUrl: imageUrl || undefined,
        commissionTier: tier as "basic" | "premium" | "flagship",
      };
      if (existingStore) {
        return api.stores.update(body as any);
      }
      return api.stores.create(body);
    },
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(existingStore ? t("common.save") : t("merchant.applicationSubmitted"));
        qc.invalidateQueries({ queryKey: ["store", "me"] });
        onOpenChange(false);
      } else {
        toast.error(res.error);
      }
    },
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="mx-auto max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{existingStore ? t("common.edit") + " " + t("merchant.title") : t("merchant.createStore")}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          {!existingStore && (
            <div className="rounded-lg bg-primary/5 p-3 text-xs text-muted-foreground">
              {t("merchant.applicationSubmittedDesc")}
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{t("merchant.storeName")} *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("merchant.storeName")} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("merchant.storeDescription")}</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("merchant.storeCategory")}</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="restaurant">{t("stores.restaurant")}</option>
              <option value="grocery">{t("stores.grocery")}</option>
              <option value="pharmacy">{t("stores.pharmacy")}</option>
              <option value="convenience">{t("stores.convenience")}</option>
              <option value="other">{t("stores.other")}</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("merchant.storeAddress")}</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("merchant.storeImage")}</Label>
            <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." />
          </div>
          {!existingStore && (
            <div className="space-y-1.5">
              <Label>{t("merchant.commissionTier")}</Label>
              <div className="space-y-2">
                {[
                  { key: "basic", label: t("merchant.tierBasic"), desc: t("merchant.tierBasicDesc") },
                  { key: "premium", label: t("merchant.tierPremium"), desc: t("merchant.tierPremiumDesc") },
                  { key: "flagship", label: t("merchant.tierFlagship"), desc: t("merchant.tierFlagshipDesc") },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setTier(opt.key)}
                    className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition ${
                      tier === opt.key ? "border-primary bg-primary/5" : "border-border"
                    }`}
                  >
                    <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${tier === opt.key ? "border-primary" : "border-muted-foreground/40"}`}>
                      {tier === opt.key && <div className="h-2 w-2 rounded-full bg-primary" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">{opt.label}</p>
                      <p className="text-xs text-muted-foreground">{opt.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <Button className="w-full" disabled={!name || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? t("common.loading") : existingStore ? t("common.save") : t("merchant.createStore")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function StatCard({ icon: Icon, label, value, color }: { icon: React.ElementType; label: string; value: string | number; color: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-1 p-3 sm:p-4">
        <Icon className={`h-5 w-5 ${color}`} />
        <p className="text-lg font-bold sm:text-xl">{value}</p>
        <p className="text-[10px] text-center text-muted-foreground sm:text-xs">{label}</p>
      </CardContent>
    </Card>
  );
}

function Row({ label, value, muted, bold }: { label: string; value: string; muted?: boolean; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={muted ? "text-muted-foreground" : ""}>{label}</span>
      <span className={`${bold ? "font-bold text-emerald-600" : "font-medium"}`}>{value}</span>
    </div>
  );
}
